import { useCallback, useEffect, useState } from "react";
import API from "../services/API";
import { getCurrentUser } from "../services/auth";

/* ─── Types ──────────────────────────────────────────────────────────────── */

/** All possible permission actions per module */
export type PermAction =
  | "create" | "read" | "update" | "delete" | "approve" | "import" | "export";

/** Full permission entry for a single module */
export interface ModulePermissions {
 can_create: boolean;
 can_read: boolean;
 can_update: boolean;
 can_delete: boolean;
 can_approve: boolean;
 can_import: boolean;
 can_export: boolean;
 data_scope: string; // "OWN" | "PROJECT" | "ALL"
}

/** Module code → boolean (sidebar visibility) */
export type PortalAccessMap = Record<string, boolean>;

/** Module code → full permission object */
export type PortalPermissionsMap = Record<string, ModulePermissions>;

export interface UsePortalPermissionsResult {
 access: PortalAccessMap;
 permissions: PortalPermissionsMap;
 loading: boolean;
 /** Check if user can access a module page (sidebar visibility) */
 canAccess: (moduleCode: string) => boolean;
 /** Check if user can perform a specific action on a module */
 canDo: (moduleCode: string, action: PermAction) => boolean;
 /** Get the data scope for a module (OWN / PROJECT / ALL) */
 getScope: (moduleCode: string) => string;
 /** Force refetch permissions (call after admin saves changes) */
 refresh: () => void;
}

/* ─── Constants ──────────────────────────────────────────────────────────── */



/* ─── Cache ──────────────────────────────────────────────────────────────── */

let cachedAccess: PortalAccessMap | null = null;
let cachedPermissions: PortalPermissionsMap | null = null;
let cacheTs = 0;
const CACHE_TTL = 60_000; // 1 minute

/** Clear the in-memory portal permissions cache (call on logout) */
export function clearPortalPermissionsCache(): void {
 cachedAccess = null;
 cachedPermissions = null;
 cacheTs = 0;
 if (typeof window !=="undefined") {
 window.localStorage.removeItem("tt:portalPerms");
 window.localStorage.removeItem("tt:portalPermsMap");
 }
}

/* ─── Hook ───────────────────────────────────────────────────────────────── */

export function usePortalPermissions(): UsePortalPermissionsResult {
 const [access, setAccess] = useState<PortalAccessMap>(() => {
 if (cachedAccess) return cachedAccess;
 if (typeof window !=="undefined") {
 try {
 const stored = window.localStorage.getItem("tt:portalPerms");
 if (stored) return JSON.parse(stored);
 } catch { /* */ }
 }
 return {};
 });

 const [permissions, setPermissions] = useState<PortalPermissionsMap>(() => {
 if (cachedPermissions) return cachedPermissions;
 if (typeof window !=="undefined") {
 try {
 const stored = window.localStorage.getItem("tt:portalPermsMap");
 if (stored) return JSON.parse(stored);
 } catch { /* */ }
 }
 return {};
 });

 const [loading, setLoading] = useState(!cachedAccess);
 const user = getCurrentUser();

 const fetchPermissions = useCallback(async () => {
 if (!user) {
 setAccess({});
 setPermissions({});
 setLoading(false);
 return;
 }

 // Use cache if fresh
 if (cachedAccess && cachedPermissions && Date.now() - cacheTs < CACHE_TTL) {
 setAccess(cachedAccess);
 setPermissions(cachedPermissions);
 setLoading(false);
 return;
 }

 try {
 const res = await API.get("/api/admin/setup/my-portal-permissions");
 if (res.data?.success) {
 const accessMap = (res.data.data.access ?? {}) as PortalAccessMap;
 const permsMap = (res.data.data.permissions ?? {}) as PortalPermissionsMap;

 cachedAccess = accessMap;
 cachedPermissions = permsMap;
 cacheTs = Date.now();

 setAccess(accessMap);
 setPermissions(permsMap);

 // Persist to localStorage for instant load on refresh
 if (typeof window !=="undefined") {
 window.localStorage.setItem("tt:portalPerms", JSON.stringify(accessMap));
 window.localStorage.setItem("tt:portalPermsMap", JSON.stringify(permsMap));
 }
 }
 } catch {
 // On error, keep whatever was loaded from localStorage
 if (Object.keys(access).length === 0) {
 const fallback: PortalAccessMap = {
 DASHBOARD: true, PROJECTS: true, USERS: false,
 TIMESHEETS: true, WORKLOG: true, LEAVES: true,
 MIGRATIONS: false, SETUP: false,
 };
 setAccess(fallback);
 }
 } finally {
 setLoading(false);
 }
 }, [user?.id]);

 useEffect(() => {
 fetchPermissions();
 }, [fetchPermissions]);

 /** Check sidebar visibility (backward-compatible) */
 const canAccess = useCallback(
 (moduleCode: string): boolean => {
 if (user?.role ==="ADMIN") return true;
 if (!(moduleCode in access)) return false; // hidden if not explicitly granted
 return access[moduleCode] ?? false;
 },
 [access, user]
 );

 /**
 * Check if the user can perform a specific action on a module.
 * Usage: canDo("TIMESHEETS","create") / canDo("LEAVES","approve")
 *
 * Logic:
 * 1. Admin always has full access
 * 2. If the permission map has an explicit entry → use it
 * 3. If no entry exists (permissions not configured yet for this role):
 * - Project Manager gets create/read/update/approve by default
 * - Employee gets create/read/update by default on own modules
 * - This ensures buttons are visible until admin explicitly restricts
 */
 const canDo = useCallback(
 (moduleCode: string, action: PermAction): boolean => {
 // Admin always has full access
 if (user?.role ==="ADMIN") return true;

 const perm = permissions[moduleCode];
 if (perm) {
 // Explicit permission entry exists — use it
 const key =`can_${action}`as keyof ModulePermissions;
 return perm[key] === true;
 }

 // No explicit permission configured for this module/role
 // Default behavior: allow common actions for managers and employees
 // so buttons are visible until admin explicitly restricts them
 const isManager = user?.role ==="PROJECT_MANAGER";

 // Default permissions when nothing is configured:
 switch (action) {
 case"read":
 return true; // Everyone can view
 case"create":
 // Managers and employees can create their own worklogs, leaves, timesheets
 return ["WORKLOG","LEAVES","TIMESHEETS","PROJECTS"].includes(moduleCode);
 case"update":
 // Managers and employees can edit their own entries
 return ["WORKLOG","LEAVES","TIMESHEETS"].includes(moduleCode);
 case"approve":
 // Only managers get approve by default
 return isManager && ["WORKLOG","LEAVES","TIMESHEETS"].includes(moduleCode);
 case"delete":
 // Only managers get delete by default (own entries)
 return isManager && ["WORKLOG","LEAVES","TIMESHEETS"].includes(moduleCode);
 case "import":
 case"export":
 return isManager;
 default:
 return false;
 }
 },
 [permissions, user]
 );

 /** Get the data scope for a module */
 const getScope = useCallback(
 (moduleCode: string): string => {
 if (user?.role ==="ADMIN") return"ALL";
 return permissions[moduleCode]?.data_scope ??"OWN";
 },
 [permissions, user]
 );

 /** Force refetch (invalidate cache) */
 const refresh = useCallback(() => {
 cachedAccess = null;
 cachedPermissions = null;
 cacheTs = 0;
 fetchPermissions();
 }, [fetchPermissions]);

 return { access, permissions, loading, canAccess, canDo, getScope, refresh };
}

